import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { unlockDraw } from "@/lib/drawLookup";
import { prepareEntry } from "@/lib/prepareEntry";
import { markExplored } from "@/lib/markExplored";
import { money, fmtDate, ordinal, canonRodeoEvent } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { useFilters } from "@/context/FiltersContext";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronDown, Loader2, ArrowRightLeft, Info as InfoIcon, Filter, Download, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle, Star, CalendarClock, FileText, ExternalLink, AlertTriangle, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getChoices, pickPerf, toggleEnteredPerf } from "@/lib/choices";
import { PickCircle } from "@/components/PickCircle";
import { toast } from "sonner";
import useTradeConflicts from "@/lib/useTradeConflicts";
import TradeFinderDialog from "@/components/TradeFinderDialog";
import PurseBreakdown, { useEnteredCounts } from "@/components/PurseBreakdown";

const WMO = {
  0: ["Clear", Sun], 1: ["Mainly clear", Sun], 2: ["Partly cloudy", Cloud], 3: ["Overcast", Cloud],
  45: ["Fog", CloudFog], 48: ["Rime fog", CloudFog],
  51: ["Light drizzle", CloudDrizzle], 53: ["Drizzle", CloudDrizzle], 55: ["Heavy drizzle", CloudDrizzle],
  61: ["Light rain", CloudRain], 63: ["Rain", CloudRain], 65: ["Heavy rain", CloudRain],
  66: ["Freezing rain", CloudRain], 67: ["Freezing rain", CloudRain],
  71: ["Light snow", CloudSnow], 73: ["Snow", CloudSnow], 75: ["Heavy snow", CloudSnow], 77: ["Snow grains", CloudSnow],
  80: ["Showers", CloudRain], 81: ["Showers", CloudRain], 82: ["Heavy showers", CloudRain],
  85: ["Snow showers", CloudSnow], 86: ["Snow showers", CloudSnow],
  95: ["Thunderstorm", CloudLightning], 96: ["Thunderstorm", CloudLightning], 99: ["Thunderstorm", CloudLightning],
};

function WeatherCard({ rodeo, m }) {
  const [daily, setDaily] = useState(null);
  const [status, setStatus] = useState("loading");
  const t = { card: m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]", title: m ? "text-slate-100" : "text-[#14181F]", sub: m ? "text-slate-400" : "text-slate-500" };

  const perfDates = [...new Set((rodeo.performances || []).map((p) => p.date).filter(Boolean))].sort();

  useEffect(() => {
    if (!rodeo.lat || !rodeo.lng || perfDates.length === 0) { setStatus("none"); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today); maxDate.setDate(maxDate.getDate() + 15);
    const inRange = perfDates.filter((d) => { const dt = new Date(d + "T00:00:00"); return dt >= today && dt <= maxDate; });
    if (inRange.length === 0) { setStatus("future"); return; }
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${rodeo.lat}&longitude=${rodeo.lng}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=auto&start_date=${inRange[0]}&end_date=${inRange[inRange.length - 1]}`;
    fetch(url).then((r) => r.json()).then((d) => {
      if (!d.daily) { setStatus("none"); return; }
      const rows = d.daily.time.map((time, i) => ({
        date: time, code: d.daily.weather_code[i],
        hi: Math.round(d.daily.temperature_2m_max[i]), lo: Math.round(d.daily.temperature_2m_min[i]),
        pop: d.daily.precipitation_probability_max[i],
      })).filter((row) => inRange.includes(row.date));
      setDaily(rows); setStatus(rows.length ? "ok" : "none");
    }).catch(() => setStatus("none"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rodeo.id]);

  if (status === "none") return null;
  return (
    <div className={`rounded-lg border ${t.card} p-5`} data-testid="weather-card">
      <div className={`font-display font-bold mb-1 flex items-center gap-2 ${t.title}`}><Cloud className="w-4 h-4 text-[#C8000F]" /> Weather — {rodeo.city}</div>
      {status === "loading" && <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>}
      {status === "future" && <p className={`text-sm ${t.sub}`}>Forecast available closer to the date (within ~2 weeks of the rodeo).</p>}
      {status === "ok" && daily && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {daily.map((row) => {
            const [label, Icon] = WMO[row.code] || ["—", Cloud];
            return (
              <div key={row.date} className={`rounded-lg px-3 py-2.5 ${m ? "bg-black/20" : "bg-[#FAFBFC]"}`} data-testid={`weather-day-${row.date}`}>
                <div className={`text-xs font-semibold ${t.title}`}>{fmtDate(row.date)}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Icon className="w-6 h-6 text-[#E68A2E]" />
                  <div>
                    <div className={`text-sm font-bold ${t.title}`}>{row.hi}° <span className="text-slate-400 font-normal">/ {row.lo}°</span></div>
                    <div className={`text-[11px] ${t.sub}`}>{label}{row.pop != null ? ` · ${row.pop}% rain` : ""}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DrawView() {
  const m = useIsMobile();
  const { events } = useFilters();
  const { user } = useAuth();
  const isFanUser = user?.account_type === "fan";
  const [favIds, setFavIds] = useState(user?.favorite_rodeos || []);
  const { id } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const tabFromUrl = { draw: "Draw", trade: "Draw", standings: "Standings", notes: "Notes", info: "Info", entries: "Entries", "ground-rules": "Ground Rules", groundrules: "Ground Rules", listing: "PRCA Listing", pbj: "PRCA Listing" }[(sp.get("tab") || "").toLowerCase()] || "Info";
  const [rodeo, setRodeo] = useState(null);
  const [draw, setDraw] = useState(null);
  const [tab, setTab] = useState(tabFromUrl);
  const [showRaw, setShowRaw] = useState(false);
  const [perfId, setPerfId] = useState(null);
  const [trade, setTrade] = useState(null); // {event, loading, data}
  const [showAll, setShowAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [drawStatus, setDrawStatus] = useState(null);
  const [drawLocked, setDrawLocked] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);
  const [choices, setChoices] = useState(getChoices());
  const [pbj, setPbj] = useState(null);
  const [watched, setWatched] = useState(false);
  const conflict = useTradeConflicts().conflictFor(id);
  const enteredCounts = useEnteredCounts(id);
  useEffect(() => { if (conflict && (sp.get("tab") || "").toLowerCase() === "trade") setTrade(true); }, [conflict?.performance_id]);
  useEffect(() => {
    const sync = () => setChoices(getChoices());
    window.addEventListener("rp-choices-changed", sync);
    return () => window.removeEventListener("rp-choices-changed", sync);
  }, []);
  useEffect(() => {
    markExplored("draw_lookup");
    api.get(`/prca/rodeo/${id}/pbj`).then((r) => setPbj(r.data)).catch(() => setPbj(null));
    if (user) api.get("/watchlist").then((r) => setWatched((r.data || []).some((w) => w.rodeo_id === id))).catch(() => {});
  }, [id, user]);

  const toggleWatch = async () => {
    try {
      if (watched) { await api.delete(`/watchlist/${id}`); setWatched(false); toast.message("Removed from your watchlist"); }
      else { await api.post(`/watchlist/${id}`); setWatched(true); toast.success("Added to watchlist — we'll remind you before entries close"); }
      window.dispatchEvent(new Event("rp-notifs-read"));
    } catch { toast.error("Could not update watchlist"); }
  };

  const load = () => {
    api.get(`/rodeos/${id}`).then((r) => { setRodeo(r.data); setPerfId((p) => p || r.data.performances[0]?.id); });
    api.get(`/rodeos/${id}/draw`).then((r) => setDraw(r.data));
    api.get(`/rodeos/draws/status`).then((r) => setDrawStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  // Charge a draw lookup (idempotent per rodeo) the first time the rider actually views
  // the Draw tab of a rodeo that has a real draw.
  useEffect(() => {
    if (tab === "Draw" && rodeo?.real_draw) {
      unlockDraw(id).then((res) => setDrawLocked(!res.ok));
    }
  }, [tab, rodeo?.real_draw, id]);

  const importDraw = async () => {
    setImporting(true);
    try {
      const { data } = await api.post(`/rodeos/${id}/import-draw`);
      toast.success(`Pulled ${data.entries} entries across ${data.events} events`);
      setPerfId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not import draw"); }
    finally { setImporting(false); }
  };
  const importPrcaDraw = async () => {
    setImporting(true);
    try {
      const { data } = await api.post(`/rodeos/${id}/import-prca-draw`);
      if (data.imported) { toast.success(`Pulled ${data.entries} draw entries from the PRCA daysheet`); load(); }
      else toast.message(data.message || "No daysheet posted yet");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not pull daysheet"); }
    finally { setImporting(false); }
  };

  if (!rodeo || !draw) return <div className={`flex justify-center py-24 ${m ? "bg-[#0C1017] min-h-screen" : ""}`}><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;

  if (rodeo.listing) return <ListingView rodeo={rodeo} m={m} nav={nav} />;

  const selectedPerf = rodeo.performances.find((p) => p.id === perfId);
  const assoc = rodeo.associations || [];
  const isPrca = rodeo.source === "prca" || assoc.includes("PRCA");
  const tabs = ["Info", "Entries", "Draw", "Standings", "Ground Rules", ...(isPrca ? ["PRCA Listing"] : []), "Notes"];

  const openTrade = () => setTrade(true);

  // theme classes
  const t = {
    page: m ? "bg-[#0C1017] min-h-screen pb-24 text-slate-200" : "",
    barBorder: m ? "border-white/10" : "border-[#E4E7EB]",
    card: m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB]",
    cardHead: m ? "bg-[#1A1F27] border-white/10" : "bg-[#F7F8FA] border-[#E4E7EB]",
    rowBorder: m ? "border-white/10" : "border-[#F3F4F6]",
    rowHover: m ? "hover:bg-white/5" : "hover:bg-[#FAFBFC]",
    title: m ? "text-white" : "",
    contestant: m ? "text-slate-100" : "font-medium",
    subtle: m ? "text-slate-400" : "text-slate-500",
  };
  const perfOn = m ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-[#0C1017] text-white border-[#0C1017]";
  const perfOff = m ? "bg-white/5 text-slate-200 border-white/10" : "bg-white border-[#E4E7EB] hover:border-slate-400";

  return (
    <div className={t.page}>
      {/* header (always dark) */}
      <div className="bg-[#0C1017] text-white px-4 sm:px-6 py-5">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-3" data-testid="draw-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">{rodeo.name}</h1>
            <div className="text-slate-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
              <span>{rodeo.city}, {rodeo.state}</span>
              {rodeo.associations.map((a) => <span key={a} className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-white/20">{a}</span>)}
            </div>
          </div>
          <div className="text-right text-sm">
            {user && isFanUser && (
              <button onClick={async () => {
                  const fav = favIds.includes(id);
                  if (fav) { setFavIds((f) => f.filter((x) => x !== id)); await api.delete(`/me/favorite-rodeos/${id}`).catch(() => {}); }
                  else { setFavIds((f) => [...f, id]); await api.post(`/me/favorite-rodeos`, { rodeo_id: id }).catch(() => {}); }
                }} data-testid="favorite-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border mb-2 transition-colors ${favIds.includes(id) ? "bg-[#C8000F] border-[#C8000F] text-white" : "bg-white/5 border-white/20 text-slate-200 hover:bg-white/10"}`}>
                <Star className={`w-3.5 h-3.5 ${favIds.includes(id) ? "fill-white" : ""}`} /> {favIds.includes(id) ? "Favorited" : "Favorite"}
              </button>
            )}
            {user && !isFanUser && (
              <button onClick={toggleWatch} data-testid="watch-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border mb-2 transition-colors ${watched ? "bg-[#C8000F] border-[#C8000F] text-white" : "bg-white/5 border-white/20 text-slate-200 hover:bg-white/10"}`}>
                <Star className={`w-3.5 h-3.5 ${watched ? "fill-white" : ""}`} /> {watched ? "Watching" : "Watch"}
              </button>
            )}
            {user && !isFanUser && (
              <button onClick={async () => { const res = await prepareEntry(id); if (res.ok) setPrepOpen(true); }} data-testid="prepare-entry-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border mb-2 ml-2 bg-[#E68A2E]/15 border-[#E68A2E]/40 text-[#E8A24E] hover:bg-[#E68A2E]/25 transition-colors">
                <FileText className="w-3.5 h-3.5" /> Prepare my entry
              </button>
            )}
            <div className="text-slate-400">EO <span className="text-white">{fmtDate(rodeo.entry_open)}</span> &nbsp; EC <span className="text-white">{fmtDate(rodeo.entry_close)}</span></div>
            <div className="font-display font-bold text-[#E68A2E] text-lg mt-1">{money(rodeo.added_money)} Added</div>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-4 overflow-x-auto">
          {tabs.map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} data-testid={`tab-${tb.toLowerCase()}`}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${tab === tb ? "bg-[#C8000F] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{tb}</button>
          ))}
        </div>
      </div>

      {/* Prepare my entry — summary sheet */}
      <Dialog open={prepOpen} onOpenChange={setPrepOpen}>
        <DialogContent className="bg-[#0C1017] text-slate-100 border border-white/10 max-w-md" data-testid="prepare-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Your entry sheet · {rodeo.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Everything you need to enter — have this ready when you call or enter online.</DialogDescription>
          </DialogHeader>
          {(() => {
            const ch = choices[id] || {};
            const p1 = rodeo.performances.find((p) => p.id === ch.pref1);
            const p2 = rodeo.performances.find((p) => p.id === ch.pref2);
            const myEvents = [...events];
            const line = (label, value) => (
              <div className="flex justify-between gap-3"><span className="text-slate-500">{label}</span><span className="text-slate-200 text-right">{value}</span></div>
            );
            return (
              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto text-sm">
                {line("Where", `${rodeo.city}${rodeo.state ? ", " + rodeo.state : ""}`)}
                {line("Sanction", (rodeo.associations || []).join(" · ") || "—")}
                {line("Entries open", fmtDate(rodeo.entry_open))}
                {line("Entries close", fmtDate(rodeo.entry_close))}
                {line("Your events", myEvents.length ? myEvents.join(", ") : "All (set your events in filters)")}
                <div>
                  <div className="text-[11px] uppercase font-bold text-slate-500 mb-1 mt-1">Your performance picks</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#C8000F] text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">1</span>{p1 ? `${p1.label} · ${fmtDate(p1.date)}${p1.time ? " · " + p1.time : ""}` : <span className="text-slate-500">Not set</span>}</div>
                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-white/10 text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">2</span>{p2 ? `${p2.label} · ${fmtDate(p2.date)}${p2.time ? " · " + p2.time : ""}` : <span className="text-slate-500">Not set</span>}</div>
                  </div>
                </div>
                {rodeo.ground_rules && (
                  <div>
                    <div className="text-[11px] uppercase font-bold text-slate-500 mb-1 mt-1">Entry fees / ground rules</div>
                    <div className="text-[13px] text-slate-300 whitespace-pre-wrap">{rodeo.ground_rules}</div>
                  </div>
                )}
                {rodeo.website_url && <a href={rodeo.website_url} target="_blank" rel="noreferrer" data-testid="prep-enter-online" className="flex items-center justify-center gap-1.5 bg-[#C8000F] text-white font-semibold py-2.5 rounded-lg mt-1"><ExternalLink className="w-4 h-4" /> Enter online</a>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* performance selector */}
      {tab === "Draw" && (
        <div className={`border-b ${t.barBorder} px-4 sm:px-6 py-3 overflow-x-auto`}>
          <div className="flex gap-2 min-w-max">
            {rodeo.performances.map((p) => {
              const on = p.id === perfId;
              return (
                <button key={p.id} onClick={() => setPerfId(p.id)} data-testid={`perf-col-${p.id}`}
                  className={`px-4 py-2 rounded-lg border text-center min-w-[120px] ${on ? perfOn : perfOff}`}>
                  <div className="font-display font-bold text-sm">{p.label}</div>
                  <div className={`text-[11px] ${on ? "text-slate-300" : t.subtle}`}>{fmtDate(p.date)} · {p.time}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 py-5">
        {tab === "Draw" && !rodeo.real_draw && (() => {
          const assoc = rodeo.associations || [];
          const isCpra = assoc.includes("CPRA");
          const isPrca = !isCpra && (rodeo.source === "prca" || assoc.includes("PRCA"));
          const isCca = !isCpra && (rodeo.source === "cca" || assoc.includes("CCA"));
          return (
            <div className={`max-w-xl mx-auto text-center rounded-lg border border-dashed p-10 ${m ? "border-white/15 text-slate-300" : "border-[#D7DBE0] text-slate-500"}`} data-testid="draw-not-posted">
              <Download className="w-8 h-8 mx-auto mb-3 opacity-60" />
              <div className={`font-display font-bold text-lg mb-1 ${m ? "text-white" : "text-[#14181F]"}`}>Draw not posted yet</div>
              {isCpra && (
                <>
                  <p className="text-sm">The official draw for this rodeo hasn't been pulled in yet.</p>
                  {user?.role === "admin" && (
                    <button onClick={importDraw} disabled={importing} data-testid="import-draw-btn"
                      className="mt-5 inline-flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
                      {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import Draw from CPRA
                    </button>
                  )}
                </>
              )}
              {isPrca && (
                <>
                  <p className="text-sm">PRCA doesn't publish a single draw feed — each rodeo posts its own daysheet on its committee website, usually 1–3 days out. To see <b>your</b> draw here automatically, enter this rodeo and connect your PRCA account.</p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    {user?.role === "admin" && (
                      <button onClick={importPrcaDraw} disabled={importing} data-testid="import-prca-draw-btn"
                        className="inline-flex items-center gap-2 bg-[#003087] hover:bg-[#00246a] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
                        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Pull PRCA daysheet
                      </button>
                    )}
                    <button onClick={() => nav("/season")} data-testid="prca-connect-cta"
                      className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-lg border ${m ? "border-white/20 text-slate-200 hover:bg-white/5" : "border-[#D7DBE0] text-slate-700 hover:bg-slate-50"}`}>
                      Connect PRCA in My Season
                    </button>
                    {rodeo.website_url && (
                      <a href={rodeo.website_url} target="_blank" rel="noreferrer" data-testid="prca-website-link"
                        className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-lg border ${m ? "border-white/20 text-slate-200 hover:bg-white/5" : "border-[#D7DBE0] text-slate-700 hover:bg-slate-50"}`}>
                        View rodeo website
                      </a>
                    )}
                  </div>
                </>
              )}
              {isCca && (
                <p className="text-sm">The CCA doesn't publish draw sheets, so entry order isn't available for this rodeo. Results are posted here after the rodeo runs.</p>
              )}
              {!isCpra && !isPrca && !isCca && (
                <p className="text-sm">The official draw for this rodeo hasn't been pulled in yet.</p>
              )}
            </div>
          );
        })()}

        {tab === "Draw" && rodeo.real_draw && drawLocked && (
          <div className="max-w-xl mx-auto text-center rounded-lg border border-dashed p-10 border-[#D7DBE0] text-slate-500" data-testid="draw-paywall">
            <p className="text-sm">You've used your free draw lookup. Unlock this draw with 1 credit, or subscribe for unlimited.</p>
            <button onClick={() => nav("/choose-plan")} data-testid="draw-see-plans" className="mt-3 bg-[#C8000F] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">See plans</button>
          </div>
        )}
        {tab === "Draw" && rodeo.real_draw && !drawLocked && (() => {
          const hasFilter = events.size > 0;
          const evMatch = new Set([...events].map(canonRodeoEvent));
          const shownEvents = draw.events.filter((ev) => showAll || !hasFilter || evMatch.has(canonRodeoEvent(ev.event)));
          const fmtStamp = (s) => s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
          return (
          <div className="space-y-4">
            {drawStatus?.last_run && (
              <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${t.subtle}`} data-testid="draw-freshness">
                <span>Draws last updated <b className={m ? "text-slate-300" : "text-slate-700"}>{fmtStamp(drawStatus.last_run)}</b></span>
                {drawStatus.next_run && <span>· Next update <b className={m ? "text-slate-300" : "text-slate-700"}>{fmtStamp(drawStatus.next_run)}</b></span>}
                <span className="opacity-70">· Auto-refreshes 10 AM &amp; 6 PM MT</span>
              </div>
            )}
            {hasFilter && (
              <div className={`flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 ${m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB] bg-[#F7F8FA]"}`} data-testid="event-filter-bar">
                <span className={`flex items-center gap-2 text-xs ${t.subtle}`}>
                  <Filter className="w-3.5 h-3.5" />
                  {showAll ? "Showing all events" : `Showing your events: ${[...events].join(", ")}`}
                </span>
                <button onClick={() => setShowAll((v) => !v)} data-testid="toggle-event-filter"
                  className={`text-xs font-semibold whitespace-nowrap ${m ? "text-[#F26D77]" : "text-[#C8000F]"} hover:underline`}>
                  {showAll ? "Show my events" : "Show all events"}
                </button>
              </div>
            )}
            {shownEvents.length === 0 ? (
              <div className={`rounded-lg border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`} data-testid="no-events-match">
                This rodeo doesn't run any of your selected events. <button onClick={() => setShowAll(true)} className="font-semibold text-[#C8000F] hover:underline">Show all events</button>
              </div>
            ) : shownEvents.map((ev) => {
              const rows = ev.rows.filter((r) => r.performance_id === perfId);
              return (
                <div key={ev.event} className={`rounded-lg border ${t.card} overflow-hidden`} data-testid={`event-block-${ev.event.replace(/\s/g, "-")}`}>
                  <div className={`flex items-center justify-between px-4 py-3 border-b ${t.cardHead}`}>
                    <div className={`font-display font-bold uppercase text-sm tracking-wide ${t.title}`}>{ev.event}</div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${t.subtle}`}>{rows.length} up · {ev.total} entered</span>
                      {conflict && (conflict.events || []).some((e) => canonRodeoEvent(e) === canonRodeoEvent(ev.event)) && (
                      <button onClick={openTrade} data-testid={`trade-btn-${ev.event.replace(/\s/g, "-")}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#F26D77] lg:text-[#C8000F] hover:underline">
                        <ArrowRightLeft className="w-3.5 h-3.5" /> Trade
                      </button>
                      )}
                    </div>
                  </div>
                  {rows.length === 0 ? (
                    <div className={`px-4 py-4 text-sm ${t.subtle}`}>No contestants up in {selectedPerf?.label} for this event.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`text-left text-[11px] uppercase tracking-wide text-slate-400 border-b ${t.rowBorder}`}>
                          <th className="px-4 py-2 w-10">#</th>
                          {ev.team ? <><th className="px-4 py-2">Header</th><th className="px-4 py-2">Heeler</th></> : <><th className="px-4 py-2">Contestant</th><th className="px-4 py-2">Hometown</th></>}
                          <th className="px-4 py-2 text-right">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} className={`border-b ${t.rowBorder} ${t.rowHover}`} data-testid={`draw-row-${ev.event.replace(/\s/g, "-")}-${i + 1}`}>
                            <td className="px-4 py-2 text-slate-400 font-mono align-top">{i + 1}</td>
                            {ev.team ? (
                              <>
                                <td className="px-4 py-2 align-top">
                                  <div className={t.contestant}>{r.header}</div>
                                  <div className={`text-xs ${t.subtle}`}>{r.hometown}</div>
                                </td>
                                <td className="px-4 py-2 align-top">
                                  <div className={t.contestant}>{r.heeler}</div>
                                  <div className={`text-xs ${t.subtle}`}>{r.heeler_hometown}</div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className={`px-4 py-2 align-top ${t.contestant}`}>{r.contestant}</td>
                                <td className={`px-4 py-2 align-top ${t.subtle}`}>{r.hometown}</td>
                              </>
                            )}
                            <td className="px-4 py-2 align-top text-right whitespace-nowrap" data-testid={`draw-result-${i + 1}`}>
                              {(r.amount_won > 0 || r.result_value) ? (
                                <>
                                  {ordinal(r.result_place) && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-[#3B6B1E]/15 text-[#3B6B1E] mr-1">{ordinal(r.result_place)}</span>}
                                  {r.result_value && <span className={`text-xs ${t.subtle}`}>{r.result_value}</span>}
                                  {r.amount_won > 0 && <div className="text-[13px] font-bold text-[#E68A2E]">{money(r.amount_won)}</div>}
                                </>
                              ) : <span className="text-slate-300">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
          );
        })()}

        {tab === "Info" && (
          <div className="max-w-3xl space-y-5">
            <WeatherCard rodeo={rodeo} m={m} />

            <GroundRules rodeo={rodeo} m={m} />

            {(rodeo.purses?.length > 0 || rodeo.added_money > 0) && (
              <div className={`rounded-lg border ${t.card} p-5`} data-testid="info-tab-money">
                <div className={`font-display font-bold mb-3 flex items-center justify-between ${t.title}`}>
                  <span>Purse &amp; Added Money</span>
                  {rodeo.added_money > 0 && <span className="text-[#E68A2E]">{money(rodeo.added_money)} total</span>}
                </div>
                <PurseBreakdown rodeo={rodeo} entered={enteredCounts} dark={m} testid="info-tab-purse" />
              </div>
            )}

            {(rodeo.entry_info || rodeo.entry_fees?.length > 0) && (
              <div className={`rounded-lg border ${t.card} p-5`} data-testid="info-tab-enter">
                <div className={`font-display font-bold mb-2 ${t.title}`}>How to Enter</div>
                {rodeo.entry_info && <p className={`text-sm leading-relaxed whitespace-pre-line mb-3 ${m ? "text-slate-300" : "text-slate-600"}`}>{rodeo.entry_info}</p>}
                {rodeo.entry_fees?.length > 0 && (
                  <>
                    <div className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${t.subtle}`}>Entry fees by event</div>
                    <div className="space-y-1">
                      {rodeo.entry_fees.map((p, i) => (
                        <div key={i} className={`flex items-center justify-between text-sm py-1.5 border-b ${t.rowBorder} last:border-0`}>
                          <span className={m ? "text-slate-300" : "text-slate-600"}>{p.event}</span>
                          <span className={`font-semibold ${t.title}`}>{money(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {(rodeo.committee_contact || rodeo.rodeo_office || rodeo.rodeo_grounds || rodeo.arena || rodeo.main_stock || rodeo.timed_stock) && (
              <div className={`rounded-lg border ${t.card} p-5`} data-testid="info-tab-contacts">
                <div className={`font-display font-bold mb-3 ${t.title}`}>Rodeo Details</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[["Committee contact", rodeo.committee_contact], ["Rodeo office", rodeo.rodeo_office],
                    ["Rodeo grounds", rodeo.rodeo_grounds], ["Arena", rodeo.arena],
                    ["Stock contractor", rodeo.main_stock], ["Timed-event stock", rodeo.timed_stock]]
                    .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}>
                      <div className={`text-[10px] font-bold uppercase tracking-wide ${t.subtle}`}>{k}</div>
                      <div className={`mt-0.5 ${m ? "text-slate-200" : "text-slate-700"}`}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {rodeo.ground_rules && (
              <div className={`rounded-lg border ${t.card} p-5`}>
                <div className={`font-display font-bold mb-2 ${t.title}`}>Ground Rules</div>
                <p className={`text-sm leading-relaxed whitespace-pre-line ${m ? "text-slate-300" : "text-slate-600"}`}>{rodeo.ground_rules}</p>
              </div>
            )}

            <div className={`rounded-lg border ${t.card} p-5`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`font-display font-bold ${t.title}`}>Performance Schedule</div>
                <div className={`text-[11px] ${t.subtle}`}>Tap <span className="text-[#C8000F] font-bold">⊕</span> to set 1st / 2nd choice</div>
              </div>
              {(rodeo.perf_order || rodeo.slack_order) && (
                <div className={`text-sm mb-3 space-y-1 ${m ? "text-slate-300" : "text-slate-600"}`}>
                  {rodeo.perf_order && <div><span className={`font-semibold ${t.subtle}`}>Performance order:</span> {rodeo.perf_order}</div>}
                  {rodeo.slack_info && <div><span className={`font-semibold ${t.subtle}`}>Slack:</span> {rodeo.slack_info}</div>}
                  {rodeo.slack_order && <div><span className={`font-semibold ${t.subtle}`}>Slack order:</span> {rodeo.slack_order}</div>}
                </div>
              )}
              <div className="space-y-1.5">
                {rodeo.performances.map((p) => {
                  const ch = choices[rodeo.id] || {};
                  const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                  const ent = !!(ch.entered || {})[p.id];
                  return (
                    <div key={p.id} data-testid={`detail-perf-row-${p.id}`}
                      className={`flex items-center justify-between text-sm py-2 px-2 rounded-md ${ent ? (m ? "bg-[#16A34A]/15 ring-1 ring-[#16A34A]/50" : "bg-[#E9F6EC] ring-1 ring-[#16A34A]/40") : num === 1 ? (m ? "bg-[#166534]/15 ring-1 ring-[#166534]/40" : "bg-[#EAF3EC] ring-1 ring-[#166534]/30") : ""}`}>
                      <span className="flex items-center gap-2.5 min-w-0">
                        <PickCircle num={num} entered={ent} onClick={() => setChoices(pickPerf(rodeo.id, p.id))} onToggleEntered={() => setChoices(toggleEnteredPerf(rodeo.id, p.id))} testid={`detail-pick-${p.id}`} />
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.type === "slack" ? (m ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700") : (m ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700")}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
                        <span className="truncate">{p.label}</span>
                      </span>
                      <span className={`${t.subtle} shrink-0 pl-2`}>{fmtDate(p.date)} · {p.time}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {rodeo.details_text && (
              <div className={`rounded-lg border ${t.card}`} data-testid="info-tab-full-details">
                <button onClick={() => setShowRaw((v) => !v)} data-testid="info-tab-full-details-toggle"
                  className={`flex items-center justify-between w-full px-5 py-4 font-display font-bold ${t.title}`}>
                  <span>Full rodeo details (from CPRA)</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                </button>
                {showRaw && (
                  <p className={`px-5 pb-5 text-sm leading-relaxed whitespace-pre-line border-t ${t.rowBorder} pt-3 ${m ? "text-slate-300" : "text-slate-600"}`}>{rodeo.details_text}</p>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "Standings" && <Standings m={m} rodeo={rodeo} />}
        {tab === "Notes" && (
          <div className={`max-w-3xl rounded-lg border ${t.card} p-5 text-sm space-y-2 ${m ? "text-slate-300" : "text-slate-600"}`}>
            <div className={`font-display font-bold mb-1 ${m ? "text-white" : "text-[#14181F]"}`}>Notes</div>
            <p>• "Available to Trade" means a contestant is entered here but is <b>not up</b> at any other rodeo that day, so they can move into the selected performance.</p>
            <p>• The draw is final once posted. This list updates as new entries or schedule changes occur.</p>
            <p>• Current draw shows where each contestant is currently placed (Perf or Slack).</p>
          </div>
        )}
        {tab === "Entries" && <EntriesTab rodeo={rodeo} pbj={pbj} m={m} t={t} user={user} watched={watched} onWatch={toggleWatch} />}
        {tab === "Ground Rules" && <GroundRulesTab rodeo={rodeo} pbj={pbj} m={m} t={t} />}
        {tab === "PRCA Listing" && <ListingTab pbj={pbj} m={m} t={t} />}
      </div>

      <TradeFinderDialog rodeo={trade ? rodeo : null} conflict={conflict} onClose={() => setTrade(null)} dark={m} />
    </div>
  );
}

function ListingView({ rodeo, m, nav }) {
  const wrap = m ? "bg-[#0C1017] min-h-screen pb-24 text-slate-200" : "min-h-screen";
  const card = m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  return (
    <div className={wrap} data-testid="listing-view">
      <div className="bg-[#0C1017] text-white px-4 sm:px-6 py-5">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-3" data-testid="draw-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">{rodeo.name}</h1>
        <div className="text-slate-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
          <span>{[rodeo.city, rodeo.province, rodeo.country].filter(Boolean).join(" · ")}</span>
          {(rodeo.associations || []).map((a) => <span key={a} className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-white/20">{a}</span>)}
        </div>
      </div>
      <div className="px-4 sm:px-6 py-5 max-w-2xl space-y-4">
        <div className={`rounded-lg border ${card} p-5`}>
          <div className={`font-display font-bold mb-3 ${m ? "text-white" : ""}`}>Event Details</div>
          <dl className="text-sm space-y-2">
            {rodeo.dates_text && <div className="flex justify-between gap-4"><dt className={sub}>Dates</dt><dd className="font-medium text-right">{rodeo.dates_text}</dd></div>}
            {rodeo.event_type && <div className="flex justify-between gap-4"><dt className={sub}>Type</dt><dd className="font-medium text-right">{rodeo.event_type}</dd></div>}
            {rodeo.primary_association && <div className="flex justify-between gap-4"><dt className={sub}>Association</dt><dd className="font-medium text-right">{rodeo.primary_association}</dd></div>}
            <div className="flex justify-between gap-4"><dt className={sub}>Country</dt><dd className="font-medium text-right">{rodeo.country}</dd></div>
            <div className="flex justify-between gap-4"><dt className={sub}>Province / State</dt><dd className="font-medium text-right">{rodeo.province || "Unspecified"}</dd></div>
          </dl>
          {rodeo.website_url && (
            <a href={rodeo.website_url} target="_blank" rel="noreferrer" data-testid="listing-website-link"
              className="mt-4 inline-flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
              <InfoIcon className="w-4 h-4" /> Official Website
            </a>
          )}
        </div>
        <div className={`rounded-lg border border-dashed p-4 text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
          This is a directory listing from the Rodeo Pilot master database. Entry windows, performances and the live draw aren't available yet — visit the official website above for the schedule.
        </div>
      </div>
    </div>
  );
}

function GroundRules({ rodeo, m }) {
  const [rules, setRules] = useState(null);
  useEffect(() => {
    if (!rodeo?.name) return;
    api.get(`/prca/ground-rules?rodeo=${encodeURIComponent(rodeo.name)}`)
      .then((r) => setRules((r.data && r.data[0]?.rules) || rodeo.ground_rules || ""))
      .catch(() => setRules(rodeo.ground_rules || ""));
  }, [rodeo?.name, rodeo?.ground_rules]);
  if (!rules) return null;
  const card = m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB] bg-white";
  return (
    <div className={`rounded-lg border ${card} p-5`} data-testid="info-tab-ground-rules">
      <div className={`font-display font-bold uppercase text-sm tracking-wide mb-2 ${m ? "text-white" : "text-[#14181F]"}`}>Ground Rules</div>
      <div className={`text-sm whitespace-pre-line ${m ? "text-slate-300" : "text-slate-600"}`}>{rules}</div>
    </div>
  );
}

function pbjTimeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} day${Math.floor(s / 86400) === 1 ? "" : "s"} ago`;
}

function SourceBadge({ source, verifiedAt, url, m }) {
  return (
    <div className={`flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] ${m ? "text-slate-400" : "text-slate-500"}`} data-testid="pbj-source-badge">
      <ShieldCheck className="w-3.5 h-3.5 text-[#5FA83B]" />
      <span>Source: <b className={m ? "text-slate-200" : "text-slate-700"}>{source || "PRCA ProRodeo Business Journal"}</b></span>
      {verifiedAt && <span>· verified {pbjTimeAgo(verifiedAt)}</span>}
      {url && <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#C8000F] hover:underline">pbj.prorodeo.org <ExternalLink className="w-3 h-3" /></a>}
    </div>
  );
}

function Countdown({ closeIso, closeTime, m }) {
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  if (!closeIso) return null;
  const [y, mo, d] = closeIso.split("-").map(Number);
  const target = new Date(y, mo - 1, d, 23, 59, 0);
  const diff = target - new Date();
  const closed = diff <= 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const urgent = !closed && days <= 3;
  const box = m ? "bg-black/25" : "bg-[#FAFBFC]";
  if (closed) return <div className={`text-sm font-semibold ${m ? "text-slate-400" : "text-slate-500"}`} data-testid="entry-countdown-closed">Entries have closed.</div>;
  return (
    <div className="flex items-center gap-3" data-testid="entry-countdown">
      {[[days, days === 1 ? "day" : "days"], [hours, hours === 1 ? "hour" : "hours"]].map(([v, l], i) => (
        <div key={i} className={`rounded-lg px-4 py-2.5 text-center ${box}`}>
          <div className={`font-display text-3xl font-extrabold tabular-nums ${urgent ? "text-[#C8000F]" : m ? "text-white" : "text-[#14181F]"}`}>{v}</div>
          <div className={`text-[10px] font-bold uppercase tracking-wide ${m ? "text-slate-400" : "text-slate-500"}`}>{l}</div>
        </div>
      ))}
      <div className={`text-sm ${m ? "text-slate-300" : "text-slate-600"}`}>
        until entries close{closeTime ? <> at <b className={m ? "text-white" : "text-[#14181F]"}>{closeTime}</b></> : ""}
      </div>
    </div>
  );
}

function ChangeList({ changes, m }) {
  if (!changes || !changes.length) return null;
  const fl = { close_date: "entry close date", close_time: "entry close time", open_date: "entry open date", open_time: "entry open time", ground_rules: "ground rules" };
  return (
    <div className={`rounded-lg border p-4 ${m ? "border-[#E68A2E]/30 bg-[#E68A2E]/10" : "border-[#F6D9B8] bg-[#FDF6EE]"}`} data-testid="pbj-changes">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-[#E68A2E]" />
        <span className={`font-display font-bold text-sm ${m ? "text-white" : "text-[#14181F]"}`}>Recent PRCA changes</span>
      </div>
      <div className="space-y-1.5">
        {changes.slice(0, 8).map((c, i) => (
          <div key={i} className={`text-xs ${m ? "text-slate-300" : "text-slate-600"}`}>
            <b className={m ? "text-slate-100" : "text-slate-800"}>{fl[c.field] || c.field}</b>: "{c.old || "—"}" → "<b className="text-[#C8000F]">{c.new || "—"}</b>" <span className="opacity-60">· {pbjTimeAgo(c.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntriesTab({ rodeo, pbj, m, t, user, watched, onWatch }) {
  const entries = pbj?.entries || [];
  const primary = entries[0] || (rodeo.entry_close ? { close_date: rodeo.entry_close, open_date: rodeo.entry_open, event: "All events", source: null } : null);
  return (
    <div className="max-w-3xl space-y-5" data-testid="entries-tab">
      <div className={`rounded-lg border ${t.card} p-5`}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className={`font-display font-bold flex items-center gap-2 ${t.title}`}><CalendarClock className="w-5 h-5 text-[#C8000F]" /> Entry Window</div>
          {user && (
            <button onClick={onWatch} data-testid="entries-watch-toggle"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${watched ? "bg-[#C8000F] border-[#C8000F] text-white" : m ? "bg-white/5 border-white/15 text-slate-200" : "bg-white border-[#E4E7EB] text-slate-700"}`}>
              <Star className={`w-3.5 h-3.5 ${watched ? "fill-white" : ""}`} /> {watched ? "Watching" : "Watch & remind me"}
            </button>
          )}
        </div>
        {primary ? (
          <>
            <Countdown closeIso={primary.close_date} closeTime={primary.close_time} m={m} />
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <div className={`text-[10px] font-bold uppercase tracking-wide ${t.subtle}`}>Entries open</div>
                <div className={`text-sm font-semibold mt-0.5 ${t.title}`}>{primary.open_date ? fmtDate(primary.open_date) : "—"}{primary.open_time ? ` · ${primary.open_time}` : ""}</div>
              </div>
              <div>
                <div className={`text-[10px] font-bold uppercase tracking-wide ${t.subtle}`}>Entries close</div>
                <div className={`text-sm font-semibold mt-0.5 ${t.title}`}>{primary.close_date ? fmtDate(primary.close_date) : "—"}{primary.close_time ? ` · ${primary.close_time}` : ""}</div>
              </div>
            </div>
          </>
        ) : (
          <div className={`text-sm ${t.subtle}`}>No entry window on file yet for this rodeo.</div>
        )}
        <div className="mt-4"><SourceBadge source={entries.length > 0 ? pbj?.source : "Rodeo Pilot database"} verifiedAt={primary?.last_verified_at} url={primary?.source_url} m={m} /></div>
      </div>

      {entries.length > 1 && (
        <div className={`rounded-lg border ${t.card} p-5`}>
          <div className={`font-display font-bold mb-3 ${t.title}`}>All entry deadlines</div>
          <div className="space-y-2">
            {entries.map((e, i) => (
              <div key={i} className={`flex items-center justify-between text-sm py-2 border-b ${t.rowBorder} last:border-0`}>
                <span className={m ? "text-slate-300" : "text-slate-600"}>{e.event || "All events"}</span>
                <span className={`font-semibold ${t.title}`}>{e.close_date ? fmtDate(e.close_date) : "—"}{e.close_time ? ` · ${e.close_time}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <ChangeList changes={(pbj?.changes || []).filter((c) => ["close_date", "close_time", "open_date", "open_time"].includes(c.field))} m={m} />
    </div>
  );
}

function GroundRulesTab({ rodeo, pbj, m, t }) {
  const gr = pbj?.ground_rules || [];
  const listing = pbj?.listing;
  const text = (listing?.parsed?.ground_rules) || (gr[0]?.parsed?.ground_rules) || (gr[0]?.raw_text) || rodeo.ground_rules || "";
  const verifiedAt = listing?.last_updated_at || gr[0]?.last_updated_at;
  const srcUrl = listing?.source_url || gr[0]?.source_url;
  const hasSrc = Boolean(listing?.parsed?.ground_rules || gr.length);
  return (
    <div className="max-w-3xl space-y-4" data-testid="ground-rules-tab">
      <div className={`rounded-lg border ${t.card} p-5`}>
        <div className={`font-display font-bold mb-3 ${t.title}`}>Ground Rules</div>
        {text ? <p className={`text-sm leading-relaxed whitespace-pre-line ${m ? "text-slate-300" : "text-slate-600"}`}>{text}</p>
          : <p className={`text-sm ${t.subtle}`}>No ground rules posted yet. Check the official rodeo website closer to the event.</p>}
        {hasSrc && <div className="mt-4"><SourceBadge source={pbj?.source} verifiedAt={verifiedAt} url={srcUrl} m={m} /></div>}
      </div>
      <ChangeList changes={(pbj?.changes || []).filter((c) => c.field === "ground_rules")} m={m} />
    </div>
  );
}

function ListingTab({ pbj, m, t }) {
  const l = pbj?.listing;
  if (!l) return (
    <div className={`max-w-3xl rounded-lg border ${t.card} p-6 text-sm ${t.subtle}`} data-testid="listing-tab-empty">
      No PRCA long listing has been captured for this rodeo yet.
    </div>
  );
  const p = l.parsed || {};
  const fields = [
    ["Classification", p.classification], ["Added money", p.added_money], ["Entry fee", p.entry_fee],
    ["Events", p.events], ["Performances", p.performances], ["Permit", p.permit],
    ["Stock contractor", p.stock_contractor], ["Arena", p.arena], ["Arena address", p.arena_address],
    ["Timezone", p.timezone],
  ].filter(([, v]) => v);
  return (
    <div className="max-w-3xl space-y-4" data-testid="listing-tab">
      <div className={`rounded-lg border ${t.card} p-5`}>
        <div className="flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-[#C8000F]" /><span className={`font-display font-bold ${t.title}`}>PRCA Listing</span></div>
        {fields.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-4">
            {fields.map(([k, v]) => (
              <div key={k}>
                <div className={`text-[10px] font-bold uppercase tracking-wide ${t.subtle}`}>{k}</div>
                <div className={`text-sm mt-0.5 ${m ? "text-slate-200" : "text-slate-700"}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <SourceBadge source={pbj?.source} verifiedAt={l.last_updated_at} url={l.source_url} m={m} />
      </div>
      {l.raw_text && (
        <div className={`rounded-lg border ${t.card} p-5`}>
          <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${t.subtle}`}>Full listing (as published)</div>
          <pre className={`text-xs leading-relaxed whitespace-pre-wrap font-mono ${m ? "text-slate-300" : "text-slate-600"}`} data-testid="listing-raw">{l.raw_text}</pre>
        </div>
      )}
    </div>
  );
}

function Standings({ m, rodeo }) {
  const assoc = rodeo?.associations || [];
  const isPrca = !assoc.includes("CPRA") && (rodeo?.source === "prca" || assoc.includes("PRCA"));
  if (isPrca) return <PrcaDrawStandings m={m} />;
  return <CpraDrawStandings m={m} />;
}

function PrcaDrawStandings({ m }) {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState("AA");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/prca/events").then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);
  useEffect(() => {
    setRows(null);
    api.get(`/prca/standings?event=${active}&type=world`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [active]);
  const chipOn = m ? "bg-[#003087] text-white border-[#003087]" : "bg-[#003087] text-white border-[#003087]";
  const chipOff = m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#E4E7EB] hover:border-slate-400";
  const card = m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB]";
  const head = m ? "bg-[#1A1F27] border-white/10 text-white" : "bg-[#F7F8FA] border-[#E4E7EB]";
  const rowB = m ? "border-white/10 hover:bg-white/5" : "border-[#F3F4F6] hover:bg-[#FAFBFC]";
  const evTabs = events.length ? events : [{ abbrev: "AA", name: "All-Around" }];
  const curName = (evTabs.find((e) => e.abbrev === active) || {}).name || active;
  return (
    <div className="max-w-4xl" data-testid="standings-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {evTabs.map((e) => (
          <button key={e.abbrev} onClick={() => setActive(e.abbrev)} data-testid={`standings-ev-${e.abbrev}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${active === e.abbrev ? chipOn : chipOff}`}>{e.name}</button>
        ))}
      </div>
      {rows === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#003087]" /></div> : (
        <div className={`rounded-lg border ${card} overflow-hidden`}>
          <div className={`px-4 py-3 border-b font-display font-bold uppercase text-sm tracking-wide ${head}`}>{curName} — PRCA World Standings</div>
          <table className="w-full text-sm">
            <thead><tr className={`text-left text-[11px] uppercase tracking-wide text-slate-400 border-b ${m ? "border-white/10" : "border-[#EEF0F2]"}`}>
              <th className="px-4 py-2 w-10">#</th><th className="px-4 py-2">Contestant</th><th className="px-4 py-2">Hometown</th><th className="px-4 py-2 text-right">Earnings</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.place}-${i}`} className={`border-b ${rowB}`}>
                  <td className="px-4 py-2 text-slate-400 font-mono">{r.place}</td>
                  <td className={`px-4 py-2 ${m ? "text-slate-100" : "font-medium"}`}>{r.name}</td>
                  <td className={`px-4 py-2 ${m ? "text-slate-400" : "text-slate-500"}`}>{r.hometown}</td>
                  <td className="px-4 py-2 text-right font-semibold">{money(r.earnings)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-400">No standings for this event yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-2">Source: prorodeo.com · World standings</div>
    </div>
  );
}

function CpraDrawStandings({ m }) {
  const [groups, setGroups] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/standings").then((r) => {
      setGroups(r.data);
      if (r.data.length) setActive(r.data[0].event);
    });
  }, []);
  if (!groups) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>;
  if (!groups.length) return (
    <div className={`max-w-3xl rounded-lg border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`} data-testid="standings-empty">
      No season standings imported yet. An admin can pull the latest Rodeo Canada standings from <span className="font-medium">Settings → Import Standings</span>.
    </div>
  );
  const current = groups.find((g) => g.event === active) || groups[0];
  const chipOn = m ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-[#0C1017] text-white border-[#0C1017]";
  const chipOff = m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#E4E7EB] hover:border-slate-400";
  const card = m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB]";
  const head = m ? "bg-[#1A1F27] border-white/10 text-white" : "bg-[#F7F8FA] border-[#E4E7EB]";
  const rowB = m ? "border-white/10 hover:bg-white/5" : "border-[#F3F4F6] hover:bg-[#FAFBFC]";
  return (
    <div className="max-w-4xl" data-testid="standings-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {groups.map((g) => (
          <button key={g.event} onClick={() => setActive(g.event)} data-testid={`standings-ev-${g.event.replace(/\s/g, "-")}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${active === g.event ? chipOn : chipOff}`}>
            {g.event}
          </button>
        ))}
      </div>
      <div className={`rounded-lg border ${card} overflow-hidden`}>
        <div className={`px-4 py-3 border-b font-display font-bold uppercase text-sm tracking-wide ${head}`}>{current.event} — Season Standings</div>
        <table className="w-full text-sm">
          <thead><tr className={`text-left text-[11px] uppercase tracking-wide text-slate-400 border-b ${m ? "border-white/10" : "border-[#EEF0F2]"}`}>
            <th className="px-4 py-2 w-10">#</th><th className="px-4 py-2">Contestant</th><th className="px-4 py-2">Hometown</th>
            <th className="px-4 py-2 text-center">Rodeos</th><th className="px-4 py-2 text-right">Earnings</th></tr></thead>
          <tbody>
            {current.rows.map((r) => (
              <tr key={r.rank} className={`border-b ${rowB}`}>
                <td className="px-4 py-2 text-slate-400 font-mono">{r.rank}</td>
                <td className={`px-4 py-2 ${m ? "text-slate-100" : "font-medium"}`}>{r.name}</td>
                <td className={`px-4 py-2 ${m ? "text-slate-400" : "text-slate-500"}`}>{r.hometown}</td>
                <td className={`px-4 py-2 text-center ${m ? "text-slate-400" : "text-slate-500"}`}>{r.entered}</td>
                <td className="px-4 py-2 text-right font-semibold">{money(r.earnings)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400 mt-2">Source: rodeocanada.com/standings</div>
    </div>
  );
}
